import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Building2, MapPin, Calendar, FileText, Link as LinkIcon, DollarSign, GraduationCap, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";

const RESEARCH_FIELDS = [
  "Computer Science", "Biology", "Physics", "Chemistry", "Medicine",
  "Engineering", "Mathematics", "Environmental Science", "Neuroscience",
  "Economics", "Psychology", "Astronomy", "Materials Science", "Genetics",
  "Immunology", "Pharmacology", "Statistics", "Biochemistry", "Ecology",
  "Other"
];

const COUNTRIES = [
  "United States", "United Kingdom", "Germany", "France", "Switzerland",
  "Netherlands", "Canada", "Australia", "Japan", "China", "Singapore",
  "Sweden", "Denmark", "Belgium", "Austria", "Spain", "Italy", "Other"
];

export default function PostJobPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    institution: "",
    department: "",
    location: "",
    country: "United States",
    field: "Computer Science",
    job_type: "postdoc",
    description: "",
    requirements: "",
    salary_range: "",
    deadline: "",
    source_url: ""
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.institution || !formData.department || !formData.location || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const jobData = {
        ...formData,
        requirements: formData.requirements.split("\n").filter(r => r.trim())
      };

      const response = await fetch(`${API}/jobs/user-submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(jobData),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success("Job posted successfully!");
        navigate(`/jobs/${result.job_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to post job");
      }
    } catch (error) {
      console.error("Error posting job:", error);
      toast.error("Failed to post job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <GraduationCap className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-white mb-4">Sign in to Post a Job</h1>
          <p className="text-gray-400 mb-6">You need to be signed in to post job listings.</p>
          <Button onClick={() => navigate("/login")} className="bg-[#800020] hover:bg-[#600018]">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <CardTitle className="text-white text-2xl font-serif">Post a Job</CardTitle>
                <CardDescription className="text-gray-400">
                  Share a job opportunity with the academic community
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Position Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Position Type *
                  </Label>
                  <Select 
                    value={formData.job_type} 
                    onValueChange={(v) => handleChange("job_type", v)}
                  >
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white" data-testid="job-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="postdoc">Postdoctoral Position</SelectItem>
                      <SelectItem value="job">Research Job</SelectItem>
                      <SelectItem value="fellowship">Fellowship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Research Field *
                  </Label>
                  <Select 
                    value={formData.field} 
                    onValueChange={(v) => handleChange("field", v)}
                  >
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white" data-testid="job-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {RESEARCH_FIELDS.map(field => (
                        <SelectItem key={field} value={field}>{field}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Job Title */}
              <div>
                <Label className="text-gray-300">Position Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  placeholder="e.g., Postdoctoral Research Fellow in Machine Learning"
                  required
                  data-testid="job-title-input"
                />
              </div>

              {/* Institution & Department */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Institution *
                  </Label>
                  <Input
                    value={formData.institution}
                    onChange={(e) => handleChange("institution", e.target.value)}
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    placeholder="e.g., Stanford University"
                    required
                    data-testid="job-institution-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Department *</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => handleChange("department", e.target.value)}
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    placeholder="e.g., Department of Computer Science"
                    required
                    data-testid="job-department-input"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    City *
                  </Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    placeholder="e.g., Stanford, CA"
                    required
                    data-testid="job-location-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Country *</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(v) => handleChange("country", v)}
                  >
                    <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white" data-testid="job-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {COUNTRIES.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Job Description *
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-[150px]"
                  placeholder="Describe the position, responsibilities, research focus, and what makes this opportunity unique..."
                  required
                  data-testid="job-description-input"
                />
              </div>

              {/* Requirements */}
              <div>
                <Label className="text-gray-300">Requirements (one per line)</Label>
                <Textarea
                  value={formData.requirements}
                  onChange={(e) => handleChange("requirements", e.target.value)}
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  placeholder="PhD in relevant field&#10;Strong programming skills&#10;Publication record&#10;Excellent communication skills"
                  data-testid="job-requirements-input"
                />
              </div>

              {/* Optional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Salary Range
                  </Label>
                  <Input
                    value={formData.salary_range}
                    onChange={(e) => handleChange("salary_range", e.target.value)}
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    placeholder="e.g., $60,000 - $80,000"
                    data-testid="job-salary-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Application Deadline
                  </Label>
                  <Input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => handleChange("deadline", e.target.value)}
                    className="mt-2 bg-white/5 border-white/10 text-white"
                    data-testid="job-deadline-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" />
                    Application URL
                  </Label>
                  <Input
                    type="url"
                    value={formData.source_url}
                    onChange={(e) => handleChange("source_url", e.target.value)}
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    placeholder="https://..."
                    data-testid="job-url-input"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#800020] hover:bg-[#600018] text-white font-semibold py-3 rounded-sm"
                  data-testid="submit-job-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Posting Job...
                    </>
                  ) : (
                    <>
                      <Briefcase className="w-5 h-5 mr-2" />
                      Post Job
                    </>
                  )}
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                By posting, you confirm this is a legitimate job opportunity.
                <br />
                Posted by: {user.email}
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
